import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Mail, Smartphone } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";

// v68.52 — step-up unlock: authenticator code, or one-time email code.
export default function VaultUnlock({ status, onUnlocked }) {
  const [method, setMethod] = useState(status.totp_enabled ? "totp" : "email");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    try { await axios.post(`${API_BASE}/vault/email-code`); setSent(true); setCode(""); toast.success(`Code sent to ${status.email}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API_BASE}/vault/unlock`, { method, code }); toast.success("Vault unlocked for this session"); onUnlocked(); }
    catch (err) { toast.error(formatApiError(err)); setCode(""); } finally { setBusy(false); }
  };
  const switchTo = (m) => { setMethod(m); setCode(""); setSent(false); };
  const needsSend = method === "email" && !sent;

  return (
    <div className="card p-6 max-w-sm mx-auto text-center space-y-4" data-testid="vault-unlock">
      <Lock size={26} className="mx-auto text-[var(--accent)]" />
      <div>
        <div className="font-heading text-xl" style={{ fontWeight: 500 }}>Credentials vault is locked</div>
        <div className="text-[12px] text-[var(--ink-3)] mt-1">
          {method === "totp" ? "Enter the 6-digit code from your authenticator app." : sent ? `Enter the 6-digit code we emailed to ${status.email}.` : `We'll email a one-time code to ${status.email}.`}
          {" "}It stays open until you log out.
        </div>
      </div>
      {needsSend ? (
        <button onClick={sendCode} disabled={busy} className="btn-primary w-full justify-center" data-testid="vault-send-email-code">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email me a code
        </button>
      ) : (
        <form onSubmit={verify} className="space-y-3">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus
            placeholder="••••••" data-testid="vault-code-input" autoComplete="one-time-code"
            className="w-full text-center font-mono text-2xl tracking-[0.5em] bg-white border border-[var(--border)] rounded-md py-2.5 focus:outline-none focus:border-[var(--ink)]" />
          <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full justify-center" data-testid="vault-unlock-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Unlock vault
          </button>
          {method === "email" && <button type="button" onClick={sendCode} disabled={busy} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] underline" data-testid="vault-resend-code">Send a new code</button>}
        </form>
      )}
      <div className="text-[11px] text-[var(--ink-3)] border-t border-[var(--border)] pt-3">
        {method === "totp" ? (
          <button onClick={() => switchTo("email")} className="inline-flex items-center gap-1 hover:text-[var(--ink)]" data-testid="vault-use-email"><Mail size={11} /> Email me a one-time code instead</button>
        ) : status.totp_enabled ? (
          <button onClick={() => switchTo("totp")} className="inline-flex items-center gap-1 hover:text-[var(--ink)]" data-testid="vault-use-totp"><Smartphone size={11} /> Use my authenticator app</button>
        ) : (
          <a href="/settings/security" className="inline-flex items-center gap-1 hover:text-[var(--ink)]" data-testid="vault-setup-totp"><Smartphone size={11} /> Set up an authenticator app (faster, more secure)</a>
        )}
      </div>
    </div>
  );
}
